import { MutationCache, QueryCache, QueryClient, type QueryMeta } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, apiErrorMessage } from '@/lib/api';
import { qk } from '@/lib/query-keys';

const ERROR_TOAST_DEDUP_MS = 3_000;
const ERROR_TOAST_DURATION_MS = 6_000;

const PUBLIC_PATH_PREFIXES = ['/login', '/invite/', '/reset-password'];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export type CreateQueryClientOptions = {
  /** Appelé sur un 401 hors des routes publiques, avec le chemin courant à restaurer après login. */
  onUnauthenticated?: ((redirect: string) => void) | undefined;
  /** Désactive les toasts d'erreur (tests). */
  silent?: boolean | undefined;
};

export function createQueryClient(options: CreateQueryClientOptions = {}): QueryClient {
  const recentErrorToasts = new Map<string, number>();

  let client: QueryClient;
  const handleError = (error: unknown, meta: QueryMeta | undefined) => {
    if (error instanceof ApiError && error.status === 401) {
      const { pathname, search } = globalThis.location;
      if (!isPublicPath(pathname)) {
        // Session tombée : la copie en cache de `/me` ne doit pas renvoyer l'utilisateur vers `/`
        // depuis `/login` (`beforeLoad` lit `ensureQueryData(qk.me)`).
        client.removeQueries({ queryKey: qk.me, exact: true });
        options.onUnauthenticated?.(`${pathname}${search}`);
      }
      return;
    }
    // `meta: { toast: false }` : l'écran affiche l'erreur lui-même (formulaire, dialogue).
    if (options.silent || meta?.toast === false) return;
    const code = error instanceof ApiError ? error.code : 'unknown';
    const now = Date.now();
    const last = recentErrorToasts.get(code);
    if (last !== undefined && now - last < ERROR_TOAST_DEDUP_MS) return;
    recentErrorToasts.set(code, now);
    toast.error(apiErrorMessage(error), { duration: ERROR_TOAST_DURATION_MS });
  };

  client = new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => handleError(error, query.meta) }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => handleError(error, mutation.meta),
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 300_000,
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
        refetchOnWindowFocus: true,
      },
    },
  });
  return client;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { toast?: boolean };
    mutationMeta: { toast?: boolean };
  }
}
